import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, MenuItem, Box, Typography, CircularProgress, Alert,
} from "@mui/material";
import { PersonAddOutlined } from "@mui/icons-material";
import AppDatePicker from "../../Components/DatePicker/AppDatePicker";
import { fetchLeadPrefill, createLead, PRIORITY_OPTIONS } from "./emailHubV3Api";
import {
  leadFormFields, leadFormSectionLabel,
  splitLeadFormValues, leadConfig,
} from "../LeadSystem/leadVertical";

// Event-type / hall / guest-count / budget lists were removed here — the
// EmailHub "Create Lead from Thread" flow now shares the same vertical
// field configuration as CreateLeadDialog / EditLeadDialog. Autism sees
// program + child + location + preferred-schedule; venue keeps the
// original event / hall / guests / budget inputs.

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: number;
  onCreated: (lead: any) => void;
}

const emptyVerticalValues = (): Record<string, string> =>
  Object.fromEntries(leadFormFields.map(f => [f.key, ""]));

const CreateLeadModal: React.FC<Props> = ({ open, onClose, threadId, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<{
    customer_name: string; email: string; phone: string; organization: string;
    notes: string; priority: string;
    vertical: Record<string, string>;
  }>({
    customer_name: "", email: "", phone: "", organization: "",
    notes: "", priority: "medium",
    vertical: emptyVerticalValues(),
  });

  useEffect(() => {
    if (open && threadId) {
      setLoading(true);
      setError("");
      fetchLeadPrefill(threadId)
        .then((data: any) => {
          setForm((prev) => {
            // Prefill the vertical map: on the venue vertical the AI's
            // event_type / event_date / guest_count / budget /
            // preferred_hall values map straight into their column:*
            // slots. On autism the same AI extraction is dropped —
            // those are venue concepts and we do not want them auto-
            // seeded into autism custom_fields either. Staff can fill
            // program / child / location manually.
            const nextVertical = { ...prev.vertical };
            if (leadConfig.supportsEventFields) {
              // Venue vertical — AI returns venue keys, bind to native
              // column: slots.
              if (data.event_type)     nextVertical["column:event_type"]     = String(data.event_type);
              if (data.event_date)     nextVertical["column:event_date"]     = String(data.event_date);
              if (data.guest_count)    nextVertical["column:guest_count"]    = String(data.guest_count);
              if (data.budget)         nextVertical["column:budget_value"]   = String(data.budget);
              if (data.preferred_hall) nextVertical["column:preferred_hall"] = String(data.preferred_hall);
            } else {
              // Service vertical — AI returns semantic autism/tutoring
              // keys; bind to the matching custom: slots that the
              // splitLeadFormValues serializer will drop into
              // custom_fields on save. No venue-shaped extraction is
              // silently discarded — the prompt itself never asked for it.
              if (data.program)         nextVertical["custom:program"]         = String(data.program);
              if (data.location)        nextVertical["custom:location"]        = String(data.location);
              if (data.child_name)      nextVertical["custom:child_name"]      = String(data.child_name);
              if (data.child_age)       nextVertical["custom:child_age"]       = String(data.child_age);
              if (data.grade)           nextVertical["custom:grade"]           = String(data.grade);
              if (data.preferred_days)  nextVertical["custom:preferred_days"]  = String(data.preferred_days);
              if (data.preferred_start) nextVertical["custom:preferred_start"] = String(data.preferred_start);
            }
            return {
              ...prev,
              customer_name: data.customer_name || "",
              email:         data.email || "",
              phone:         data.phone || "",
              notes:         data.notes || "",
              vertical:      nextVertical,
            };
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, threadId]);

  const handleChange = (field: "customer_name" | "email" | "phone" | "organization" | "notes" | "priority") =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const setVertical = (key: string, val: string) =>
    setForm((prev) => ({ ...prev, vertical: { ...prev.vertical, [key]: val } }));

  const handleSave = async () => {
    if (!form.customer_name.trim()) { setError("Customer name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const { columns, customFields } = splitLeadFormValues(form.vertical);
      const payload: any = {
        customer_name: form.customer_name,
        email:         form.email,
        phone:         form.phone,
        organization:  form.organization,
        notes:         form.notes,
        priority:      form.priority,
        thread_id:     threadId,
        ...columns,
        ...(Object.keys(customFields).length ? { custom_fields: customFields } : {}),
      };
      const lead = await createLead(payload);
      onCreated(lead);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to create lead.");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
        <PersonAddOutlined sx={{ color: "#3b82f6" }} /> Create Lead from Thread
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
              AI-extracted data has been pre-filled. Adjust as needed.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Customer Name" value={form.customer_name}
                  onChange={handleChange("customer_name")} fullWidth required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Email" value={form.email}
                  onChange={handleChange("email")} fullWidth size="small" type="email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Phone" value={form.phone}
                  onChange={handleChange("phone")} fullWidth size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Organization" value={form.organization}
                  onChange={handleChange("organization")} fullWidth size="small" />
              </Grid>

              {/* Vertical-specific section header + fields — matches the
                  primary LeadSystem create dialog exactly. */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", mb: 1, mt: 0.5 }}>
                  {leadFormSectionLabel}
                </Typography>
              </Grid>
              {leadFormFields.map((f) => {
                const value = form.vertical[f.key] ?? "";
                const cols = Math.min(6, f.gridColumns ?? 6);
                if (f.type === "select") {
                  return (
                    <Grid key={f.key} item xs={12} sm={cols}>
                      <TextField label={f.label} value={value}
                        onChange={(e) => setVertical(f.key, e.target.value)}
                        fullWidth size="small" select>
                        <MenuItem value="">—</MenuItem>
                        {(f.options ?? []).map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  );
                }
                if (f.type === "date") {
                  return (
                    <Grid key={f.key} item xs={12} sm={cols}>
                      <AppDatePicker label={f.label} value={value}
                        onChange={(v: string) => setVertical(f.key, v)} fullWidth size="small" />
                    </Grid>
                  );
                }
                return (
                  <Grid key={f.key} item xs={12} sm={cols}>
                    <TextField label={f.label} value={value}
                      onChange={(e) => setVertical(f.key, e.target.value)}
                      fullWidth size="small"
                      type={f.type === "number" ? "number" : "text"}
                      multiline={f.type === "textarea"}
                      rows={f.type === "textarea" ? 3 : undefined}
                      placeholder={f.placeholder} />
                  </Grid>
                );
              })}

              <Grid item xs={12} sm={4}>
                <TextField label="Priority" value={form.priority}
                  onChange={handleChange("priority")} fullWidth size="small" select>
                  {PRIORITY_OPTIONS.map((p: string) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notes" value={form.notes}
                  onChange={handleChange("notes")} fullWidth size="small"
                  multiline rows={3} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={16} /> : <PersonAddOutlined />}
          sx={{ background: "#3b82f6", "&:hover": { background: "#2563eb" } }}>
          Create Lead
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLeadModal;
